import { useEffect, useRef, useState, useMemo } from "react";
import { VirtuosoHandle } from "react-virtuoso";
import {
  GroupedResult,
  GroupingConfig,
  ScrollManagerConfig,
  RenderContext,
} from "./types";

interface UseVirtuosoWrapperStateProps<T> {
  data: T[];
  grouping?: GroupingConfig<T>;
  scrollManager?: ScrollManagerConfig<T>;
  isLoadingMore?: boolean;
  /**
   * The reader is mid-something in this list (an item's details are open).
   * Holds the scroll position on prepend even at the top of the list, where
   * new items would otherwise push what they are reading down the viewport.
   */
  isEngaged?: boolean;
}

interface UseVirtuosoWrapperStateReturn<T> {
  /** Ref to attach to Virtuoso component */
  virtuosoRef: React.RefObject<VirtuosoHandle>;

  /** Current first item index (for scroll preservation) */
  firstItemIndex: number;

  /** Whether user is at the top of the list */
  atListTop: boolean;

  /** Callback for Virtuoso's atTopStateChange */
  handleAtTopChange: (atTop: boolean) => void;

  /** Whether to show "new items available" notification */
  showUpdateNotification: boolean;

  /** Function to scroll to top (smooth scroll) */
  scrollToTop: () => void;

  /** The grouped result (or flat result if no grouping) */
  groupedData: GroupedResult<T>;

  /** Whether this is a grouped list */
  isGrouped: boolean;

  /** Helper function to get render context for an item */
  getRenderContext: (index: number) => RenderContext;
}

/**
 * Internal hook for managing grouped virtual list state
 * Handles scroll preservation, new items notification, and grouping logic
 */
export function useVirtuosoWrapperState<T>({
  data,
  grouping,
  scrollManager,
  isLoadingMore = false,
  isEngaged = false,
}: UseVirtuosoWrapperStateProps<T>): UseVirtuosoWrapperStateReturn<T> {
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // Extract scroll config values to avoid creating new objects every render
  const scrollEnabled = scrollManager?.enabled ?? false;
  const scrollStartingIndex = scrollManager?.startingIndex ?? 9999;
  const compareFirstItem = useMemo(
    () =>
      scrollManager?.compareFirstItem ??
      ((a: T | undefined, b: T | undefined) => a === b),
    [scrollManager?.compareFirstItem]
  );

  // IMPORTANT: Only use custom firstItemIndex if scrollManager is explicitly enabled
  // Initialize with the correct value based on initial props
  // The useState callback ensures correct initialization without race conditions
  const [firstItemIndex, setFirstItemIndex] = useState(() => {
    return scrollEnabled ? scrollStartingIndex : 0;
  });

  const [atListTop, setAtListTop] = useState(true);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);

  // Determine if this is a grouped list
  const isGrouped = !!grouping;

  // Apply grouping if configured
  const groupedData = useMemo<GroupedResult<T>>(() => {
    if (grouping) {
      const result = grouping.groupBy(data);

      // Validate the grouped result
      const totalCount = result.groupCounts.reduce(
        (sum, count) => sum + count,
        0
      );
      if (totalCount !== result.items.length) {
        console.error(
          "useVirtuosoWrapperState: Mismatch between groupCounts total and items length:",
          totalCount,
          result.items.length
        );
      }

      return result;
    }

    // No grouping - return flat structure
    return {
      groups: [],
      groupCounts: [],
      items: data,
      itemToGroupMap: [],
    };
  }, [data, grouping]);

  // Track previous item count and first item for detecting prepends. These are
  // pure bookkeeping (never rendered), so they live in refs — updating them
  // must NOT trigger a re-render. Storing them as state would loop forever if
  // `compareFirstItem` can't converge (e.g. it compares by an id that is NaN,
  // since `NaN !== NaN`): the render-phase update below would fire every render.
  const prevItemCountRef = useRef(groupedData.items.length);
  const prevFirstItemRef = useRef<T | undefined>(groupedData.items[0]);

  // Scroll to top function
  const scrollToTop = () => {
    virtuosoRef.current?.scrollToIndex({
      index: 0,
      align: "start",
      behavior: "smooth",
    });
  };

  // Handle at top state changes from Virtuoso
  const handleAtTopChange = (atTop: boolean) => {
    setAtListTop(atTop);
  };

  // Get render context for an item
  const getRenderContext = (index: number): RenderContext => {
    if (!isGrouped || !groupedData.itemToGroupMap) {
      return {};
    }

    const groupIndex = groupedData.itemToGroupMap[index];
    const groupName = groupedData.groups[groupIndex];

    // Find first and last item indices in this group
    let groupStartIndex = 0;
    for (let i = 0; i < groupIndex; i++) {
      groupStartIndex += groupedData.groupCounts[i];
    }
    const groupEndIndex = groupStartIndex + groupedData.groupCounts[groupIndex];

    return {
      groupIndex,
      groupName,
      isFirstInGroup: index === groupStartIndex,
      isLastInGroup: index === groupEndIndex - 1,
    };
  };

  // Synchronous detection of prepended items during render.
  // Uses React's "store information from previous renders" pattern so that
  // firstItemIndex and data change in the SAME render cycle — eliminating
  // the one-frame flash that useEffect would cause.
  if (scrollEnabled) {
    const currentCount = groupedData.items.length;
    const currentFirstItem = groupedData.items[0];

    if (
      currentCount !== prevItemCountRef.current ||
      !compareFirstItem(currentFirstItem, prevFirstItemRef.current)
    ) {
      const diff = currentCount - prevItemCountRef.current;
      const firstItemChanged = !compareFirstItem(
        currentFirstItem,
        prevFirstItemRef.current
      );

      // Being at the top normally means "show me the newest", so prepends are
      // allowed to push the list down. That stops being true the moment the
      // reader opens a row: the story they are reading is then carried down the
      // viewport mid-sentence. While something is open the position is held and
      // the notification offers the jump back, exactly as when scrolled away.
      const holdPosition = !atListTop || isEngaged;

      if (diff > 0 && firstItemChanged && holdPosition && !isLoadingMore) {
        setFirstItemIndex((prev) => prev - diff);
        setShowUpdateNotification(true);
      }

      prevItemCountRef.current = currentCount;
      prevFirstItemRef.current = currentFirstItem;
    }
  }

  // Hide notification when user scrolls to top
  useEffect(() => {
    if (atListTop) {
      setShowUpdateNotification(false);
    }
  }, [atListTop]);

  return {
    virtuosoRef,
    firstItemIndex,
    atListTop,
    handleAtTopChange,
    showUpdateNotification,
    scrollToTop,
    groupedData,
    isGrouped,
    getRenderContext,
  };
}
